// Recipient selection state for the Request Access modal.
//
// The picker accepts both individual people and IdP-rule groups in one
// selection set. Group ids are prefixed (see `groupKey`) so they can't
// collide with member UUIDs in the flat `string[]` of selected ids. At
// submit time groups are expanded to their pre-intersected `memberIds`.

import { useEffect, useMemo, useState } from "react";
import type { MFMember } from "state/members/member";
import type {
  EligibleRecipient,
  EligibleRecipientGroup,
  EligibleRecipients,
} from "state/members/access_v2/catalogs";
import type { BasketRecipient, ResolvedRecipient } from "./BasketDrawer";
import type { PickerPerson } from "./RecipientPicker";

const GROUP_PREFIX = "group:";
const groupKey = (id: string) => `${GROUP_PREFIX}${id}`;
const stripGroupKey = (id: string) => id.slice(GROUP_PREFIX.length);

const displayName = (m: {
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
}) => {
  const fl = `${m.firstName ?? ""} ${m.lastName ?? ""}`.trim();
  return fl || m.name || m.email || "Unknown";
};

const subtitleFor = (r: {
  jobTitle?: string;
  department?: string;
  email?: string;
}) =>
  r.jobTitle && r.department
    ? `${r.jobTitle} · ${r.department}`
    : r.jobTitle || r.department || r.email;

const toPickerPerson = (
  r: EligibleRecipient,
  meId: string | undefined,
): PickerPerson => ({
  id: r.id,
  name: displayName(r),
  suffix: r.id === meId ? "(Myself)" : undefined,
  avatarSrc: r.imageUrl,
  subtitle: subtitleFor(r),
});

// "rule:department" / "rule:all" → human label for the group's subtitle.
const groupSubtitleKind = (source: string) => {
  if (source === "rule:department") return "Department";
  if (source === "rule:all") return "Everyone in the org";
  return "Group";
};

const toPickerGroup = (g: EligibleRecipientGroup): PickerPerson => {
  const n = g.memberIds.length;
  return {
    id: groupKey(g.id),
    name: g.name,
    isGroup: true,
    groupCount: n,
    suffix: `(${n})`,
    subtitle: `${groupSubtitleKind(g.source)} · ${n} ${n === 1 ? "member" : "members"}`,
  };
};

export interface RecipientSelection {
  /** Everyone selectable in the picker: Myself, then groups, then people. */
  pickerPeople: PickerPerson[];
  /** The currently-selected recipients, in selection order. */
  recipients: BasketRecipient[];
  /** Recipients with groups expanded to member ids — for submission. */
  resolvedRecipients: ResolvedRecipient[];
  selectedIds: string[];
  pickerOpen: boolean;
  setPickerOpen: (open: boolean) => void;
  toggle: (id: string) => void;
  remove: (id: string) => void;
}

export const useRecipientSelection = (
  me: MFMember | undefined,
  eligible: EligibleRecipients,
): RecipientSelection => {
  const [selectedIds, setSelectedIds] = useState<string[]>(
    me ? [me.id] : [],
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  // Seed "Myself" once `me` resolves — the first render may have had
  // `me === undefined` (query still loading) and left the list empty.
  useEffect(() => {
    if (me && selectedIds.length === 0) setSelectedIds([me.id]);
  }, [me, selectedIds.length]);

  const pickerPeople = useMemo<PickerPerson[]>(() => {
    if (!me) return [];
    // `me` is an MFMember but structurally satisfies EligibleRecipient for
    // the fields toPickerPerson reads; passing `me.id` as meId makes it
    // pick up the "(Myself)" suffix automatically.
    const meAsRecipient = toPickerPerson(me, me.id);
    const others = eligible.people
      .filter((r) => r.id !== me.id)
      .map((r) => toPickerPerson(r, me.id));
    // Dynamic-rule groups surface first so the "request for <dept>" /
    // "everyone" shortcuts come before the individual list.
    return [meAsRecipient, ...eligible.groups.map(toPickerGroup), ...others];
  }, [me, eligible]);

  const recipients = useMemo<BasketRecipient[]>(
    () =>
      selectedIds
        .map((id) => pickerPeople.find((p) => p.id === id))
        .filter((p): p is PickerPerson => Boolean(p)),
    [selectedIds, pickerPeople],
  );

  const resolvedRecipients = useMemo<ResolvedRecipient[]>(() => {
    const groupsById = new Map(eligible.groups.map((g) => [g.id, g]));
    return recipients.map((r) =>
      r.isGroup
        ? { ...r, memberIds: groupsById.get(stripGroupKey(r.id))?.memberIds ?? [] }
        : { ...r, memberIds: [r.id] },
    );
  }, [recipients, eligible.groups]);

  return {
    pickerPeople,
    recipients,
    resolvedRecipients,
    selectedIds,
    pickerOpen,
    setPickerOpen,
    toggle: (id) =>
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      ),
    remove: (id) => setSelectedIds((prev) => prev.filter((x) => x !== id)),
  };
};
